/**
 * Is a logo too dark for this dark app? (The user, 2026-10-27: "Tomorrowland
 * is not visible and Coachella is nearly seen" — then "this need to be fixed
 * for everything".) A third of the raves' picture logos are black lettering
 * on a transparent ground — drawn for a white page — and on the app's
 * near-black they vanish. A dark one is shown lightened (`invert` +
 * `hue-rotate`: black turns white, a colour keeps its hue) — every picture
 * logo goes through `LogoImg` (`visitor/Logo.tsx`), which asks here. An SVG
 * logo, and a card's logo, is a mask painted in a colour, so never needs this.
 *
 * `index.html`'s launch screen does the same with an inline copy of
 * `isDarkLogo` — it runs before this bundle exists. Keep the two in step.
 */
import { useEffect, useState } from 'react';

/** Mean brightness of the visible pixels below this is "dark" (measured on every dev logo: dark ones sit under 0.2, legible ones over 0.35). */
export const DARK_LOGO = 0.3;

/** RGBA pixels → whether what is drawn (alpha ≥ 64) is dark on average. Nothing drawn: not dark. */
export function isDarkLogo(rgba: ArrayLike<number>): boolean {
  let n = 0;
  let sum = 0;
  for (let k = 0; k + 3 < rgba.length; k += 4) {
    if (rgba[k + 3]! < 64) continue;
    sum += (0.2126 * rgba[k]! + 0.7152 * rgba[k + 1]! + 0.0722 * rgba[k + 2]!) / 255;
    n++;
  }
  return n > 0 && sum / n < DARK_LOGO;
}

/**
 * Loads `src` a second time, CORS-clean (media is served with
 * `Access-Control-Allow-Origin: *`; a data: URL needs nothing), draws it
 * small and measures it. Anything that fails — no canvas, a tainted image —
 * answers false: the logo is shown as it is.
 */
export function measureDarkLogo(src: string): Promise<boolean> {
  const known = measured.get(src);
  if (known) return known;
  const answer = measure(src);
  measured.set(src, answer);
  return answer;
}

/** One measurement per logo per page: the header's logo is on every screen. */
const measured = new Map<string, Promise<boolean>>();

function measure(src: string): Promise<boolean> {
  return new Promise((done) => {
    if (typeof Image === 'undefined') return done(false);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const c = document.createElement('canvas');
        c.width = c.height = 32;
        const x = c.getContext('2d');
        if (!x) return done(false);
        x.drawImage(img, 0, 0, 32, 32);
        done(isDarkLogo(x.getImageData(0, 0, 32, 32).data));
      } catch {
        done(false);
      }
    };
    img.onerror = () => done(false);
    img.src = src;
  });
}

/** Whether `src` should be lightened — false until it is measured (a light logo never flickers). */
export function useDarkLogo(src: string | null | undefined): boolean {
  const [dark, setDark] = useState(false);
  useEffect(() => {
    let live = true;
    setDark(false);
    if (src) void measureDarkLogo(src).then((d) => { if (live) setDark(d); });
    return () => { live = false; };
  }, [src]);
  return dark;
}
